package com.essntl.features.request.ui.addeditrequest.components

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.TransformOrigin
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.pointer.PointerIcon
import androidx.compose.ui.input.pointer.pointerHoverIcon
import com.essntl.uikit.components.file.FileViewer
import com.essntl.features.request.ui.addeditrequest.AddEditRequestEvent
import com.essntl.features.request.ui.addeditrequest.AddEditRequestNavigation
import com.essntl.features.request.ui.addeditrequest.AddEditRequestState
import com.essntl.features.request.ui.addeditrequest.AddEditRequestValidation
import com.essntl.uikit.components.button.outline.EsMediumOutlineButton
import com.essntl.uikit.components.button.secondary.EsLargeSecondaryButton
import com.essntl.uikit.components.button.tinted.EsMediumTintedButton
import com.essntl.uikit.components.dropdown.EsInputDropdownMenu
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.icons.EsLargeIcon
import com.essntl.uikit.components.icons.EsMediumIcon
import com.essntl.uikit.components.input.EsInput
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography
import kotlinx.coroutines.delay

@Composable
fun RequestTravelAgentInformation(
    state: AddEditRequestState,
    validation: AddEditRequestValidation,
    onEvent: (AddEditRequestEvent) -> Unit,
    onNavigation: (AddEditRequestNavigation) -> Unit,
    enabled: Boolean,
) {
    val request = state.request.value
    val isLoading = state.addEditRequest.isLoading

    var isExpanded by remember { mutableStateOf(true) }

    var travelAgentSearchText by rememberSaveable { mutableStateOf("") }

    var fileUrl by rememberSaveable { mutableStateOf("") }
    var isFileViewerOpened by rememberSaveable { mutableStateOf(false) }

    LaunchedEffect(travelAgentSearchText) {
        delay(500)
        onEvent(AddEditRequestEvent.SearchTravelAgent(travelAgentSearchText))
    }

    Column(
        modifier =
            Modifier
                .fillMaxWidth()
                .background(
                    color = MaterialTheme.esColorScheme.bgSecondary,
                    shape = RoundedCornerShape(EsConstants.RoundedCornerSize),
                )
                .padding(
                    vertical = 3.spacing,
                    horizontal = 2.5f.spacing,
                ),
    ) {
        Row(
            horizontalArrangement = Arrangement.SpaceBetween,
            modifier =
                Modifier
                    .fillMaxWidth(),
        ) {
            Text(
                text = "Travel Agent Information",
                color = MaterialTheme.esColorScheme.textPrimary,
                style = MaterialTheme.esTypography.s2SemiBold16,
            )

            EsLargeIcon(
                res = EsIcons.ArrowDown,
                modifier =
                    Modifier
                        .graphicsLayer {
                            transformOrigin = TransformOrigin.Center
                            rotationZ = if (isExpanded) 180f else 0f
                        }
                        .clip(CircleShape)
                        .pointerHoverIcon(PointerIcon.Hand)
                        .clickable(
                            interactionSource = remember { MutableInteractionSource() },
                            indication = null,
                        ) {
                            isExpanded = !isExpanded
                        },
            )
        }

        AnimatedVisibility(isExpanded) {
            Column(
                horizontalAlignment = Alignment.CenterHorizontally,
                modifier =
                    Modifier
                        .fillMaxWidth(),
            ) {
                Spacer(modifier = Modifier.height(3.spacing))

                EsInputDropdownMenu(
                    items = state.getAllTravelAgent.data,
                    selectedItem = request.travelAgent,
                    text = { "" },
                    onItemSelected = {
                        validation.addEditRequestForm.travelAgent.validate(it.id)
                        onEvent(
                            AddEditRequestEvent.UpdateRequestFields(
                                request.copy(
                                    travelAgent = it,
                                ),
                            ),
                        )
                    },
                    label = "Travel agent",
                    placeholder = "Enter Travel agent",
                    message = "This field is required",
                    state = validation.addEditRequestForm.travelAgent.state,
                    loading = state.getAllTravelAgent.isLoading &&
                        state.getAllTravelAgent.data.isEmpty(),
                    enabled = enabled && !isLoading,
                    topContent = {
                        Spacer(Modifier.height(0.5.spacing))
                        EsInput(
                            value = travelAgentSearchText,
                            onValueChange = {
                                travelAgentSearchText = it
                            },
                            placeholder = "Search",
                            prefix = {
                                EsMediumIcon(res = EsIcons.Search)
                            },
                            modifier = Modifier.fillMaxWidth(),
                        )

                        Spacer(Modifier.height(1.spacing))
                    },
                ) {
                    if (!state.getAllTravelAgent.isLastPage) {
                        Spacer(modifier = Modifier.height(2.spacing))

                        EsMediumOutlineButton(
                            onClick = {
                                onEvent(AddEditRequestEvent.LoadMoreTravelAgent)
                            },
                            text = "Load more",
                            loading = state.getAllTravelAgent.isLoading,
                            modifier =
                                Modifier
                                    .align(Alignment.CenterHorizontally),
                        )
                    }
                }

                Spacer(modifier = Modifier.height(2.spacing))

                request.travelAgent?.let { ta ->
                    EsMediumTintedButton(
                        text = "Edit Travel Agent",
                        onClick = {
                            onNavigation(AddEditRequestNavigation.EditTravelAgent(ta))
                        },
                        enabled = enabled && !isLoading,
                        modifier = Modifier.align(Alignment.Start),
                    )
                }

                Spacer(modifier = Modifier.height(2.spacing))

                // TODO: we cannot edit delete a TA for now.
//                if (getAllTravelAgents.isLoading)
//                    EsSpinner()
//                else
//                    getAllTravelAgents.data?.forEachIndexed { index, travelAgent ->
//                        TravelAgentItem(
//                            travelAgent = travelAgent,
//                            enabled = enabled && !isLoading,
//                            onEditTravelAgent = {
//                                toEditTravelAgentId = travelAgent.id
//                                isAddEditTravelAgentDialogExpanded = true
//                            },
//                            onDeleteTravelAgent = {
//                                 onEvent(AddEditRequestEvent.RemoveTravelAgent(it))
//                            },
//                            modifier = Modifier
//                                .fillMaxWidth(),
//                        )
//
//                        if (index != getAllTravelAgents.data?.lastIndex)
//                            Spacer(modifier = Modifier.height(1.spacing))
//                    }
//
//                Spacer(modifier = Modifier.height(2.spacing))

                EsLargeSecondaryButton(
                    onClick = {
                        onNavigation(AddEditRequestNavigation.AddTravelAgent)
                    },
                    text = "Create new travel agent",
                    suffixIconRes = EsIcons.Add,
                    enabled = enabled && !isLoading,
                    modifier =
                        Modifier
                            .fillMaxWidth(),
                )
            }
        }
    }

    FileViewer(
        fileUrl = fileUrl,
        isDialogOpened = isFileViewerOpened,
        onDismissRequest = {
            isFileViewerOpened = false
            fileUrl = ""
        },
    )
}
